<script lang="ts">
	/**
	 * One labelled row of a form: label line, the control itself, then helper or
	 * error text underneath.
	 *
	 * Every settings screen repeats the same three-part shape — a `<Label>`, the
	 * input, and a small note. Hand-writing it drifts almost immediately (the
	 * note ends up `text-sm` here, the label loses its bottom margin there), so
	 * the rhythm lives here and callers only supply the control.
	 *
	 * Two ways to supply it: pass the `children` snippet to wrap an arbitrary
	 * control, or omit it and let the field render a plain `Input` bound to
	 * `value` — the shorthand the auth and profile forms use.
	 *
	 * Long explanations go in `helpText` rather than under the field: they hide
	 * behind a `?` next to the label, so a dense form stays scannable and the
	 * detail is still one hover away. Short, always-relevant notes ("Enable for
	 * port 465") belong in `description`, which is always visible.
	 *
	 * Grouping convention, so fields breathe consistently:
	 *   <form class="space-y-4"> … </form>             — stacked fields
	 *   <div class="grid grid-cols-2 gap-4"> … </div>  — side-by-side fields
	 * Wrap a related group in a `Panel` (or a plain `widget`) to give it a title.
	 */

	import { Input, Label, Popover } from 'flowbite-svelte';
	import type { Snippet } from 'svelte';

	interface Props {
		/** Label text. Omit to render the control with no label line at all. */
		label?: string;
		/** `for`/`id` wiring — pass the same value as the control's `id`. */
		id?: string;
		/** Appends the ` *` marker the rest of the app uses for mandatory fields. */
		required?: boolean;

		/** Always-visible note under the control. Hidden while an error is shown. */
		description?: string;
		/** Legacy alias for `description`. */
		helper?: string;
		/** Longer explanation, revealed by the `?` beside the label. */
		helpText?: string;
		/** Documentation link. Takes precedence over `helpText` — one `?` only. */
		helpURL?: string;

		/**
		 * Validation message. Non-empty swaps the helper note for red error text
		 * and marks the row `aria-invalid`, so a field can go from valid to invalid
		 * without the layout shifting.
		 */
		error?: string;

		/** Extra classes for the wrapper. */
		class?: string;

		/** Button or badge pushed to the far right of the label line. */
		labelRight?: Snippet;
		/** Rich error content, overriding `error`. */
		errorSnippet?: Snippet;
		/** The control. Omit to get the built-in `Input` bound to `value`. */
		children?: Snippet;

		// ── Built-in `Input` shorthand (ignored when `children` is given) ──

		/** Bound input value. */
		value?: string;
		/** HTML input type. */
		type?:
			| 'color'
			| 'date'
			| 'datetime-local'
			| 'email'
			| 'file'
			| 'hidden'
			| 'image'
			| 'month'
			| 'number'
			| 'password'
			| 'search'
			| 'tel'
			| 'text'
			| 'time'
			| 'url'
			| 'week';
		/** Placeholder shown when the input is empty. */
		placeholder?: string;
		/** Whether the input is disabled (read-only appearance). */
		disabled?: boolean;
		/** Whether the input should receive focus when mounted. */
		autofocus?: boolean;
		/** Callback fired on the input event. */
		oninput?: (_e: Event) => void;
		/** Callback fired on the keypress event. */
		onkeypress?: (_e: KeyboardEvent) => void;
		/** Callback fired on the blur event. */
		onblur?: (_e: FocusEvent) => void;
	}

	let {
		label = '',
		id = '',
		required = false,
		description = '',
		helper = '',
		helpText = '',
		helpURL = '',
		error = '',
		class: className = '',
		labelRight,
		errorSnippet,
		children,
		value = $bindable(''),
		type = 'text',
		placeholder = '',
		disabled = false,
		autofocus = false,
		oninput,
		onkeypress,
		onblur
	}: Props = $props();

	let hasError = $derived(!!errorSnippet || error.length > 0);
	/** The always-visible note, under either spelling. */
	let resolvedDescription = $derived(description || helper);
</script>

<div class="{className} mb-4" aria-invalid={hasError ? 'true' : undefined}>
	{#if label.length > 0 || labelRight}
		<div class="mb-2 flex items-center gap-2">
			{#if label.length > 0}
				<Label for={id} class={hasError ? 'text-red-600 dark:text-red-500' : ''}
					>{label}{#if required}<span class="text-red-600 dark:text-red-500" aria-hidden="true"> *</span>{/if}</Label
				>
			{/if}

			{#if helpURL.length > 0}
				<a
					href={helpURL}
					target="_blank"
					rel="noopener"
					title={label || helpURL}
					class="text-blue-600 hover:text-primary-500 dark:text-blue-400"
				>
					<i class="fa-solid fa-circle-question"></i>
				</a>
			{:else if helpText.length > 0}
				<!-- The Popover attaches to its previous sibling, so this pair must stay adjacent. -->
				<div class="cursor-help text-blue-600 hover:text-primary-500 dark:text-blue-400">
					<i class="fa-solid fa-circle-question"></i>
				</div>
				<Popover class="z-30 w-64 text-base font-normal shadow-2xl shadow-blue-800" placement="top"
					>{helpText}</Popover
				>
			{/if}

			{#if labelRight}
				<span class="grow"></span>
				{@render labelRight()}
			{/if}
		</div>
	{/if}

	{#if children}
		{@render children()}
	{:else}
		<Input
			{id}
			{type}
			{placeholder}
			{disabled}
			{required}
			bind:value
			color={hasError ? 'red' : undefined}
			{autofocus}
			{oninput}
			{onkeypress}
			{onblur}
		/>
	{/if}

	{#if hasError}
		<p class="mt-1 text-xs text-red-600 dark:text-red-500">
			{#if errorSnippet}{@render errorSnippet()}{:else}{error}{/if}
		</p>
	{:else if resolvedDescription.length > 0}
		<p class="mt-1 text-xs text-gray-500">{resolvedDescription}</p>
	{/if}
</div>
